import React, { useState, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Highlight, PageHeader, AccentStrip, Card, SectionTitle,
  Field, StatTile, InfoBox, PrimaryButton, SecondaryButton,
  PdfExportPanel, Disclaimer, GlobalStyles,
} from "./StockUI";

// ════════════════════════════════════════════════════════════
//  TYPES
// ════════════════════════════════════════════════════════════

interface FormState {
  stock:     string;
  avgPrice:  string;
  buyQty:    string;
  sellPrice: string;
  sellQty:   string;
}

interface Case1Result {
  capital:      number;
  revenue:      number;
  profit:       number;
  profitPct:    number;
  gainPerShare: number;
  isProfit:     boolean;
}

// One pocket % scenario: e.g. keep 40% of profit in pocket
interface PocketScenario {
  pct:          number;   // 40 or 50
  pocketAmt:    number;   // profit × pct%  → cash in pocket
  reinvestAmt:  number;   // profit × (1-pct%) → reinvested at avgPrice
  freeQty:      number;   // floor(reinvestAmt / avgPrice)
  sellQtyNeeded: number;  // ceil(capital / sellPrice) — shares to sell to recover capital
  totalFreeQty: number;   // freeQty (remaining after pocket deduction)
}

interface Case2Result {
  capital:          number;
  totalProfit:      number;
  combinedValue:    number;   // capital + profit
  holdQty:          number;   // floor(combinedValue / avgPrice)
  minSellToRecover: number;   // ceil(capital / sellPrice)
  scenarios:        PocketScenario[];   // 40%, 50% options
  gainPerShare:     number;
}

// ════════════════════════════════════════════════════════════
//  UTILITIES
// ════════════════════════════════════════════════════════════

const inr = (n: number, dec = 2) =>
  "₹" + Math.abs(parseFloat(String(n))).toLocaleString("en-IN", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

const inrPdf = (n: number, dec = 2) =>
  "Rs." + Math.abs(parseFloat(String(n))).toLocaleString("en-IN", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

const sign = (n: number) => (n >= 0 ? "+" : "−");

// ════════════════════════════════════════════════════════════
//  CALCULATION LOGIC
// ════════════════════════════════════════════════════════════

function computeCase1(f: FormState): Case1Result | null {
  const avg  = parseFloat(f.avgPrice);
  const bQty = parseFloat(f.buyQty);
  const sp   = parseFloat(f.sellPrice);
  const sQty = parseFloat(f.sellQty);
  if ([avg, bQty, sp, sQty].some(isNaN) || avg <= 0 || bQty <= 0 || sp <= 0 || sQty <= 0) return null;

  const capital      = avg * bQty;
  const revenue      = sp * sQty;
  const gainPerShare = sp - avg;
  const profit       = gainPerShare * sQty;
  const profitPct    = (profit / capital) * 100;

  return { capital, revenue, profit, profitPct, gainPerShare, isProfit: profit >= 0 };
}

function computeCase2(f: FormState, c1: Case1Result): Case2Result | null {
  const avg  = parseFloat(f.avgPrice);
  const bQty = parseFloat(f.buyQty);
  const sp   = parseFloat(f.sellPrice);
  if ([avg, bQty, sp].some(isNaN) || avg <= 0 || sp <= avg) return null;

  const capital         = avg * bQty;                    // ₹20
  const gainPerShare    = sp - avg;
  const totalProfit     = c1.profit;                     // ₹10
  const combinedValue   = capital + totalProfit;         // ₹30
  const holdQty         = Math.floor(combinedValue / avg); // 15 qty
  const minSellToRecover = Math.ceil(capital / sp);      // ceil(20/3) = 7 or ceil(20/30) = 1

  // For each pocket %, compute how many shares are free
  // Logic:
  //   pocketAmt   = profit × pct%          → cash you keep
  //   reinvestAmt = profit × (1 - pct%)    → profit portion reinvested
  //   freeQty     = floor(reinvestAmt / avgPrice)   → free shares from reinvested profit
  //   sellQtyNeeded = minSellToRecover (fixed — just recover capital)
  //   totalFreeQty = holdQty - sellQtyNeeded  (all remaining after recovering capital)
  //   But from that free pool, pocketAmt worth is taken as cash, rest stays as free shares

  const scenarios: PocketScenario[] = [40, 50].map(pct => {
    const pocketAmt   = totalProfit * (pct / 100);          // e.g. 40% → ₹4
    const reinvestAmt = totalProfit * (1 - pct / 100);      // e.g. 60% → ₹6
    const freeQty     = Math.floor(reinvestAmt / avg);       // ₹6 / ₹2 = 3 free shares
    return {
      pct,
      pocketAmt,
      reinvestAmt,
      freeQty,
      sellQtyNeeded: minSellToRecover,
      totalFreeQty: freeQty,
    };
  });

  return {
    capital, totalProfit, combinedValue, holdQty,
    minSellToRecover, scenarios, gainPerShare,
  };
}

// ════════════════════════════════════════════════════════════
//  HOOK
// ════════════════════════════════════════════════════════════

function useProfitCalc() {
  const [form, setForm] = useState<FormState>({
    stock: "IOC", avgPrice: "2", buyQty: "10", sellPrice: "3", sellQty: "10",
  });
  const [case1, setCase1]         = useState<Case1Result | null>(null);
  const [case2, setCase2]         = useState<Case2Result | null>(null);
  const [error, setError]         = useState("");
  const [exporting, setExporting] = useState(false);

  const setField = (k: keyof FormState) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  const calculate = () => {
    const avg  = parseFloat(form.avgPrice);
    const bQty = parseFloat(form.buyQty);
    const sp   = parseFloat(form.sellPrice);
    const sQty = parseFloat(form.sellQty);

    if ([avg, bQty, sp, sQty].some(isNaN) || avg <= 0 || bQty <= 0 || sp <= 0 || sQty <= 0) {
      setError("Please fill in all fields with valid positive numbers."); return;
    }
    if (sQty > bQty) {
      setError("Sell quantity cannot exceed buy quantity."); return;
    }
    setError("");
    setCase1(null);
    setCase2(null);

    const r1 = computeCase1(form);
    if (!r1) return;
    setCase1(r1);

    if (sp > avg && r1.profit > 0) {
      setCase2(computeCase2(form, r1));
    }
  };

  const exportPdf = async () => {
    if (!case1) return;
    setExporting(true);
    try {
      if (!(window as any).jspdf) {
        await new Promise<void>((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
          s.onload = () => res();
          s.onerror = () => rej();
          document.head.appendChild(s);
        });
      }
      buildPdf(form, case1, case2);
    } catch {
      alert("PDF export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return { form, setField, case1, case2, error, exporting, calculate, exportPdf };
}

// ════════════════════════════════════════════════════════════
//  PDF BUILDER
// ════════════════════════════════════════════════════════════

function buildPdf(form: FormState, c1: Case1Result, c2: Case2Result | null) {
  const { jsPDF } = (window as any).jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const W = 210, M = 14, CW = W - M * 2, H = 297, FOOT = 18;
  let y = 0;

  const RED  : [number,number,number] = [204, 20,  20 ];
  const ROYAL: [number,number,number] = [65,  105, 225];
  const DARK : [number,number,number] = [26,  26,  46 ];
  const MUTED: [number,number,number] = [107, 114, 128];
  const GREEN: [number,number,number] = [22,  163, 74 ];
  const LBRD : [number,number,number] = [221, 225, 240];
  const SURF : [number,number,number] = [248, 249, 252];

  const drawFooter = () => {
    const fy = H - FOOT + 4;
    doc.setFillColor(...RED);   doc.rect(0,       H - FOOT, W * 0.5, 0.8, "F");
    doc.setFillColor(...ROYAL); doc.rect(W * 0.5, H - FOOT, W * 0.5, 0.8, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...MUTED);
    doc.text("Not financial advice. For educational purposes only.", W / 2, fy + 2, { align: "center" });
    doc.text(`Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, W / 2, fy + 6, { align: "center" });
  };

  const ensureSpace = (need: number) => {
    if (y + need > H - FOOT) { drawFooter(); doc.addPage(); y = 14; }
  };

  const section = (t: string) => {
    ensureSpace(14);
    doc.setFillColor(...RED); doc.rect(M, y, 3, 6, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...MUTED);
    doc.text(t.toUpperCase(), M + 6, y + 4.5); y += 10;
  };

  const divider = () => {
    ensureSpace(8);
    doc.setDrawColor(...LBRD); doc.setLineWidth(0.3);
    doc.line(M, y, W - M, y); y += 5;
  };

  const statRow = (items: { label: string; value: string; color?: [number,number,number] }[]) => {
    const sw = (CW - (items.length - 1) * 3) / items.length, sh = 20;
    ensureSpace(sh + 4);
    items.forEach(({ label, value, color }, i) => {
      const sx = M + i * (sw + 3), sy = y;
      doc.setFillColor(...SURF); doc.setDrawColor(...LBRD); doc.setLineWidth(0.3);
      doc.roundedRect(sx, sy, sw, sh, 2, 2, "FD");
      doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...MUTED);
      doc.text(label.toUpperCase(), sx + 4, sy + 6);
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...(color ?? DARK));
      doc.text(value, sx + 4, sy + 15);
    });
    y += sh + 5;
  };

  // ── HEADER ──
  doc.setFillColor(...RED); doc.rect(0, 0, W, 36, "F");
  doc.setFillColor(255,255,255); doc.setGState(doc.GState({ opacity: 0.06 }));
  doc.circle(W - 10, 5, 20, "F"); doc.circle(10, 30, 14, "F");
  doc.setGState(doc.GState({ opacity: 1 }));
  doc.setTextColor(255,255,255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text("Stock Profit Calculator", W / 2, 15, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text("Simple P&L  |  Free Qty Strategy  |  Pocket Analysis", W / 2, 23, { align: "center" });
  doc.setFillColor(...RED);   doc.rect(0,       36, W * 0.5, 1.5, "F");
  doc.setFillColor(...ROYAL); doc.rect(W * 0.5, 36, W * 0.5, 1.5, "F");
  y = 44;

  // ── TRADE DETAILS ──
  section("Trade Details");
  const colW2 = CW / 2;
  const tradeItems = [
    ["Stock",    form.stock.toUpperCase()],
    ["Buy Qty",  form.buyQty + " shares"],
    ["Avg Price", inrPdf(parseFloat(form.avgPrice))],
    ["Sell Price", inrPdf(parseFloat(form.sellPrice))],
    ["Sell Qty", form.sellQty + " shares"],
    ["Capital",  inrPdf(c1.capital, 0)],
  ];
  const tradeH = 8 + 3 * 14 + 8;
  ensureSpace(tradeH + 4);
  doc.setFillColor(...SURF); doc.roundedRect(M, y, CW, tradeH, 3, 3, "F");
  doc.setDrawColor(...LBRD); doc.setLineWidth(0.4); doc.roundedRect(M, y, CW, tradeH, 3, 3, "S");
  doc.setDrawColor(...LBRD); doc.setLineWidth(0.3); doc.line(M + colW2, y + 6, M + colW2, y + tradeH - 6);
  tradeItems.forEach(([label, val], i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const ix = M + col * colW2 + 10, iy = y + 8 + row * 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text(label, ix, iy);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...DARK);
    doc.text(val, ix, iy + 7);
  });
  y += tradeH + 6;

  // ── CASE 1 ──
  section("Case 1 — Simple P&L");
  statRow([
    { label: "Capital",      value: inrPdf(c1.capital, 0),  color: ROYAL },
    { label: "Revenue",      value: inrPdf(c1.revenue, 0),  color: DARK  },
    { label: "Gain / Share", value: `${sign(c1.gainPerShare)}${inrPdf(Math.abs(c1.gainPerShare))}`, color: c1.isProfit ? GREEN : RED },
  ]);
  statRow([
    { label: c1.isProfit ? "Net Profit" : "Net Loss",
      value: `${sign(c1.profit)}${inrPdf(Math.abs(c1.profit), 0)}`, color: c1.isProfit ? GREEN : RED },
    { label: "Return %",
      value: `${sign(c1.profitPct)}${Math.abs(c1.profitPct).toFixed(2)}%`, color: c1.isProfit ? GREEN : RED },
    { label: "Status", value: c1.isProfit ? "PROFIT" : "LOSS", color: c1.isProfit ? GREEN : RED },
  ]);
  divider();

  // ── CASE 2 ──
  if (c2) {
    section("Case 2 — Pocket % + Free Qty Strategy");

    // Summary
    statRow([
      { label: "Capital",        value: inrPdf(c2.capital, 0),       color: ROYAL },
      { label: "Profit",         value: inrPdf(c2.totalProfit, 0),   color: GREEN },
      { label: "Combined Value", value: inrPdf(c2.combinedValue, 0), color: DARK  },
    ]);
    statRow([
      { label: "Hold Qty (combined/avg)",    value: `${c2.holdQty} shares`,           color: DARK  },
      { label: "Min Sell to Recover Capital", value: `${c2.minSellToRecover} shares`, color: RED   },
      { label: "Gain / Share",               value: inrPdf(c2.gainPerShare),          color: GREEN },
    ]);

    // Scenarios table
    ensureSpace(14 + c2.scenarios.length * 10 + 6);
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text("POCKET SCENARIOS", M, y + 3); y += 8;

    const sCols  = ["Pocket %", "Cash in Pocket", "Reinvested", "Free Qty", "Sell to Cover"];
    const sColW  = [28, 38, 36, 28, 34];
    doc.setFillColor(...ROYAL); doc.rect(M, y, CW, 7, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(255, 255, 255);
    let tx = M;
    sCols.forEach((h, i) => { doc.text(h, tx + 3, y + 4.8); tx += sColW[i]; });
    y += 7;

    c2.scenarios.forEach((sc, idx) => {
      ensureSpace(11);
      const rH = 10;
      const bg: [number,number,number] = idx === 0 ? [235,240,255] : [255,255,255];
      doc.setFillColor(...bg); doc.rect(M, y, CW, rH, "F");
      doc.setDrawColor(...LBRD); doc.setLineWidth(0.2); doc.line(M, y + rH, M + CW, y + rH);
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...DARK);
      tx = M;
      [
        `${sc.pct}%`,
        inrPdf(sc.pocketAmt),
        inrPdf(sc.reinvestAmt),
        `${sc.freeQty} qty`,
        `${sc.sellQtyNeeded} qty`,
      ].forEach((cell, i) => { doc.text(cell, tx + 3, y + 6.5); tx += sColW[i]; });
      y += rH;
    });
    y += 5;

    // Recommendation
    const best = c2.scenarios[0]; // 40%
    const recLines = doc.splitTextToSize(
      `Sell ${best.sellQtyNeeded} share(s) at ${inrPdf(parseFloat(form.sellPrice))} to recover your capital of ${inrPdf(c2.capital, 0)}. From your profit of ${inrPdf(c2.totalProfit, 0)}, keep ${inrPdf(best.pocketAmt)} (${best.pct}%) as cash in your pocket. The remaining ${inrPdf(best.reinvestAmt)} buys you ${best.freeQty} FREE shares at avg price ${inrPdf(parseFloat(form.avgPrice))} — zero cost basis.`,
      CW - 12
    );
    const recH = 10 + recLines.length * 5;
    ensureSpace(recH + 4);
    doc.setFillColor(220, 252, 231); doc.setDrawColor(...GREEN); doc.setLineWidth(0.4);
    doc.roundedRect(M, y, CW, recH, 3, 3, "FD");
    doc.setFillColor(...GREEN); doc.rect(M, y, 3, recH, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...GREEN);
    doc.text("Recommended Strategy (40% Pocket)", M + 7, y + 6);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...DARK);
    doc.text(recLines, M + 7, y + 11);
    y += recH + 5;
    divider();
  }

  drawFooter();
  doc.save(`${form.stock.toUpperCase() || "STOCK"}_profit_report.pdf`);
}

// ════════════════════════════════════════════════════════════
//  UI PRIMITIVES (local)
// ════════════════════════════════════════════════════════════

const ErrorBanner = ({ message }: { message: string }) => (
  <p style={{
    marginTop:14, fontFamily:"var(--mono)", fontSize:12,
    color:"var(--red)", background:"rgba(204,20,20,0.06)",
    border:"1px solid rgba(204,20,20,0.2)", borderRadius:8, padding:"8px 12px",
  }}>⚠️ {message}</p>
);

// ════════════════════════════════════════════════════════════
//  SECTION COMPONENTS
// ════════════════════════════════════════════════════════════

const InputForm = ({ form, setField, error, onCalc }: {
  form: FormState;
  setField: (k: keyof FormState) => (v: string) => void;
  error: string;
  onCalc: () => void;
}) => (
  <Card className="fade-up">
    <SectionTitle>Trade Inputs</SectionTitle>
    <div className="form-grid">
      <Field label="Stock Name"        id="stock"     value={form.stock}     onChange={setField("stock")}     placeholder="e.g. IOC" />
      <Field label="Buy Qty"           id="buyQty"    type="number" value={form.buyQty}    onChange={setField("buyQty")}    placeholder="10" />
      <Field label="Avg Buy Price (₹)" id="avgPrice"  type="number" value={form.avgPrice}  onChange={setField("avgPrice")}  placeholder="2.00" step="0.01" />
      <Field label="Sell Price (₹)"    id="sellPrice" type="number" value={form.sellPrice} onChange={setField("sellPrice")} placeholder="3.00" step="0.01" />
      <Field label="Sell Qty"          id="sellQty"   type="number" value={form.sellQty}   onChange={setField("sellQty")}   placeholder="10" fullWidth />
    </div>
    {error && <ErrorBanner message={error} />}
    <div style={{ marginTop: 20 }}>
      <PrimaryButton onClick={onCalc}>⚡ Calculate Profit</PrimaryButton>
    </div>
  </Card>
);

// ── Case 1 ────────────────────────────────────────────────
const Case1Card = ({ c1, form }: { c1: Case1Result; form: FormState }) => {
  const pc = c1.isProfit ? "var(--green)" : "#dc2626";
  const bg = c1.isProfit ? "rgba(22,163,74,0.05)" : "rgba(220,38,38,0.05)";
  const bd = c1.isProfit ? "rgba(22,163,74,0.2)"  : "rgba(220,38,38,0.2)";
  return (
    <Card className="fade-up-1">
      <SectionTitle badge="CASE 1">Simple P&amp;L</SectionTitle>

      {/* Capital → Revenue banner */}
      <div style={{ background:"#f8f9fc", border:"1px solid var(--border)", borderRadius:12, padding:16, marginBottom:16, display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:12, alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:"var(--mono)", fontSize:9, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>Capital</div>
          <div style={{ fontFamily:"var(--mono)", fontSize:22, fontWeight:700, color:"var(--royal)" }}>{inr(c1.capital, 0)}</div>
          <div style={{ fontFamily:"var(--mono)", fontSize:10, color:"var(--muted)", marginTop:3 }}>{form.buyQty} × {inr(parseFloat(form.avgPrice))}</div>
        </div>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:26 }}>{c1.isProfit ? "📈" : "📉"}</div>
          <div style={{ fontFamily:"var(--mono)", fontSize:9, color:"var(--muted)", marginTop:4 }}>{sign(c1.gainPerShare)}{inr(Math.abs(c1.gainPerShare))}/share</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontFamily:"var(--mono)", fontSize:9, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>Revenue</div>
          <div style={{ fontFamily:"var(--mono)", fontSize:22, fontWeight:700, color:"var(--text)" }}>{inr(c1.revenue, 0)}</div>
          <div style={{ fontFamily:"var(--mono)", fontSize:10, color:"var(--muted)", marginTop:3 }}>{form.sellQty} × {inr(parseFloat(form.sellPrice))}</div>
        </div>
      </div>

      {/* Profit result */}
      <div style={{ background:bg, border:`1.5px solid ${bd}`, borderRadius:12, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:"var(--mono)", fontSize:10, color:pc, textTransform:"uppercase", letterSpacing:"0.12em", marginBottom:4 }}>
            {c1.isProfit ? "Net Profit" : "Net Loss"}
          </div>
          <div style={{ fontFamily:"var(--mono)", fontSize:32, fontWeight:700, color:pc, lineHeight:1 }}>
            {sign(c1.profit)}{inr(Math.abs(c1.profit), 0)}
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontFamily:"var(--mono)", fontSize:28, fontWeight:700, color:pc }}>
            {sign(c1.profitPct)}{Math.abs(c1.profitPct).toFixed(2)}%
          </div>
          <div style={{ fontFamily:"var(--mono)", fontSize:11, color:"var(--muted)", marginTop:4 }}>return on capital</div>
        </div>
      </div>

      <div className="stat-grid3" style={{ marginTop:14 }}>
        <StatTile label="Capital"      value={inr(c1.capital, 0)} />
        <StatTile label="Revenue"      value={inr(c1.revenue, 0)} />
        <StatTile label="Gain / Share" value={`${sign(c1.gainPerShare)}${inr(Math.abs(c1.gainPerShare))}`} color={pc} />
      </div>
    </Card>
  );
};

// ── Case 2 ────────────────────────────────────────────────
const Case2Card = ({ c2, form }: { c2: Case2Result; form: FormState }) => {
  if (!c2) return null;
  const avg = parseFloat(form.avgPrice);
  const sp  = parseFloat(form.sellPrice);

  return (
    <Card className="fade-up-2">
      <SectionTitle badge="CASE 2">Pocket % + Free Qty Strategy</SectionTitle>

      {/* Explanation */}
      <div style={{ background:"rgba(65,105,225,0.04)", border:"1.5px solid rgba(65,105,225,0.15)", borderRadius:12, padding:16, marginBottom:20, borderLeft:"4px solid var(--royal)" }}>
        <div style={{ fontFamily:"var(--sans)", fontWeight:700, fontSize:13, color:"var(--royal)", marginBottom:8 }}>
          How it works
        </div>
        <p style={{ fontFamily:"var(--mono)", fontSize:12, color:"var(--muted)", lineHeight:1.9 }}>
          You have <strong style={{color:"var(--text)"}}>{inr(c2.capital, 0)} capital</strong> + <strong style={{color:"var(--green)"}}>{inr(c2.totalProfit, 0)} profit</strong> = <strong style={{color:"var(--text)"}}>{inr(c2.combinedValue, 0)} combined</strong>.
          {" "}At avg price {inr(avg)}, that's <strong style={{color:"var(--royal)"}}>{c2.holdQty} qty</strong> equivalent.
          Sell just <strong style={{color:"#dc2626"}}>{c2.minSellToRecover} share(s)</strong> at {inr(sp)} to recover your full capital.
          Then from your profit, choose what % to <strong style={{color:"var(--green)"}}>keep as cash</strong> — the rest converts to <strong style={{color:"var(--green)"}}>FREE shares</strong> at your avg price.
        </p>
      </div>

      {/* Flow: Capital + Profit = Combined */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr auto 1fr", gap:8, alignItems:"center", marginBottom:20 }}>
        {([
          { label:"Capital",        val:inr(c2.capital, 0),       color:"var(--royal)" },
          { arrow:"+" },
          { label:"Profit",         val:inr(c2.totalProfit, 0),   color:"var(--green)" },
          { arrow:"=" },
          { label:"Combined → "+c2.holdQty+" qty", val:inr(c2.combinedValue, 0), color:"var(--text)" },
        ] as any[]).map((item, i) =>
          item.arrow ? (
            <div key={i} style={{ textAlign:"center", fontFamily:"var(--mono)", fontSize:20, fontWeight:700, color:"var(--muted)" }}>{item.arrow}</div>
          ) : (
            <div key={i} style={{ background:"#f8f9fc", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px", textAlign:"center" }}>
              <div style={{ fontFamily:"var(--mono)", fontSize:8, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4, lineHeight:1.4 }}>{item.label}</div>
              <div style={{ fontFamily:"var(--mono)", fontSize:15, fontWeight:700, color:item.color }}>{item.val}</div>
            </div>
          )
        )}
      </div>

      {/* Scenarios — 40% and 50% */}
      <div style={{ fontFamily:"var(--mono)", fontSize:10, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>
        Profit Pocket Scenarios
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:20 }}>
        {c2.scenarios.map((sc, i) => {
          const isFirst = i === 0;
          return (
            <div key={sc.pct} style={{
              borderRadius:14,
              border: isFirst ? "2px solid rgba(65,105,225,0.4)" : "1px solid var(--border)",
              background: isFirst ? "rgba(65,105,225,0.03)" : "#f8f9fc",
              overflow:"hidden",
            }}>
              {/* Header bar */}
              <div style={{ background: isFirst ? "var(--royal)" : "var(--border)", padding:"8px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontFamily:"var(--mono)", fontSize:11, fontWeight:700, color: isFirst ? "#fff" : "var(--muted)", letterSpacing:"0.1em" }}>
                  {sc.pct}% IN POCKET
                </span>
                {isFirst && <span style={{ fontFamily:"var(--mono)", fontSize:9, color:"rgba(255,255,255,0.8)", background:"rgba(255,255,255,0.15)", padding:"2px 8px", borderRadius:100 }}>★ SUGGESTED</span>}
              </div>

              {/* Stats */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:0 }}>
                {[
                  { label:"Cash in Pocket",   val:inr(sc.pocketAmt),    color:isFirst?"var(--royal)":"var(--text)", icon:"💰" },
                  { label:"Reinvested",        val:inr(sc.reinvestAmt),  color:"var(--text)",                       icon:"🔄" },
                  { label:"Free Qty",          val:sc.freeQty+" shares", color:"var(--green)",                      icon:"🆓" },
                  { label:"Sell to Recover",   val:sc.sellQtyNeeded+" qty", color:"#dc2626",                        icon:"📤" },
                ].map((stat, j) => (
                  <div key={j} style={{
                    padding:"14px 12px",
                    borderLeft: j > 0 ? "1px solid var(--border)" : "none",
                    display:"flex", flexDirection:"column", gap:4,
                  }}>
                    <span style={{ fontSize:14 }}>{stat.icon}</span>
                    <span style={{ fontFamily:"var(--mono)", fontSize:9, color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.08em" }}>{stat.label}</span>
                    <span style={{ fontFamily:"var(--mono)", fontSize:15, fontWeight:700, color:stat.color, lineHeight:1.2 }}>{stat.val}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommendation */}
      {(() => {
        const best = c2.scenarios[0];
        return (
          <InfoBox
            color="var(--green)"
            bg="rgba(22,163,74,0.04)"
            border="rgba(22,163,74,0.2)"
            title={`✅ Recommended — ${best.pct}% Pocket Strategy`}
          >
            {`Sell ${best.sellQtyNeeded} share(s) at ${inr(sp)} → recover capital ${inr(c2.capital, 0)}. `}
            {`From profit ${inr(c2.totalProfit, 0)}: keep ${inr(best.pocketAmt)} (${best.pct}%) as cash. `}
            {`Reinvest ${inr(best.reinvestAmt)} → get ${best.freeQty} FREE shares at avg price ${inr(avg)}. `}
            {`Those ${best.freeQty} shares cost you nothing — zero cost basis.`}
          </InfoBox>
        );
      })()}
    </Card>
  );
};

const PdfPanel = ({ onExport, isExporting }: { onExport: () => void; isExporting: boolean }) => (
  <div className="fade-up-3" style={{ background:"var(--card)", border:"1.5px dashed var(--border)", borderRadius:16, padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
    <div>
      <p style={{ fontFamily:"var(--sans)", fontWeight:700, fontSize:14, color:"var(--text)", marginBottom:3 }}>📄 Export Full Report</p>
      <p style={{ fontFamily:"var(--mono)", fontSize:11, color:"var(--muted)" }}>Download Case 1 + Case 2 analysis as PDF</p>
    </div>
    <SecondaryButton onClick={onExport} disabled={isExporting}>
      {isExporting ? <><span className="spinner">⟳</span> Generating…</> : <>⬇ Download PDF</>}
    </SecondaryButton>
  </div>
);

// ════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════

const ProfitCalculator: React.FC = () => {
  const { form, setField, case1, case2, error, exporting, calculate, exportPdf } = useProfitCalc();

  useEffect(() => {
    const t = setTimeout(calculate, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <GlobalStyles />
      <style>{`
        .stat-grid3 { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
        @media(max-width:560px){ .stat-grid3{ grid-template-columns:repeat(2,1fr); } }
        @media(max-width:360px){ .stat-grid3{ grid-template-columns:1fr; } }
        @media(max-width:519px){
          .scenario-grid{ grid-template-columns:repeat(2,1fr) !important; }
        }
      `}</style>

      <div style={{ background:"var(--bg)", color:"var(--text)", fontFamily:"var(--sans)", minHeight:"100vh", paddingBottom:48 }}>

        <div className="fade-down">
          <PageHeader
            title={<>Stock <Highlight>Profit</Highlight> Calculator</>}
            subtitle="Simple P&L · Pocket % · Free Qty Strategy"
            tag="💰 Profit Tool"
          />
          <AccentStrip />
        </div>

        <div style={{ maxWidth:720, margin:"0 auto", padding:"28px 16px 0", display:"flex", flexDirection:"column", gap:20 }}>

          <InputForm form={form} setField={setField} error={error} onCalc={calculate} />

          {case1 && (
            <>
              <Case1Card c1={case1} form={form} />

              {case2
                ? <Case2Card c2={case2} form={form} />
                : (
                  <div style={{ background:"rgba(220,38,38,0.04)", border:"1.5px solid rgba(220,38,38,0.2)", borderRadius:16, padding:20, borderLeft:"4px solid #dc2626" }}>
                    <p style={{ fontFamily:"var(--sans)", fontWeight:700, color:"#dc2626", marginBottom:4 }}>Case 2 Not Applicable</p>
                    <p style={{ fontFamily:"var(--mono)", fontSize:12, color:"var(--muted)" }}>
                      Sell price must be higher than avg buy price to unlock the free qty strategy.
                    </p>
                  </div>
                )
              }

              <PdfPanel onExport={exportPdf} isExporting={exporting} />
              <Disclaimer text="⚠️ Not financial advice. For educational purposes only. Always do your own research." />
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default ProfitCalculator;